import memoize from 'lodash/memoize';
import { Sku } from 'src/types/sku.types';
import { Cart } from 'types/cart.types';

export type TCartFlow = {
  // Cart State Utils
  isSingleServiceFlow: GenericReturnFunction<boolean>;
  getMaxAllowedItems: GenericReturnFunction<number>;
  isAtItemLimit: GenericReturnFunction<boolean>;

  // UI Helpers
  getSkuPageConfig: ({ newSkuId, existingSkuId }: { newSkuId: Sku['id']; existingSkuId?: Sku['id'] }) => { showWarning: boolean };
  getStandardHeaderConfig: () => {
    showCartIcon: boolean;
    // we need to make this accept any type of params
    onCartIconClick: BaseAnyFunction | null;
  };
};

export const MAX_ITEMS = {
  SINGLE_SERVICE: 1,
  MULTIPLE_SERVICES: Infinity,
};

export const DEFAULT_CONFIG_VALUES: TCartFlow = {
  isSingleServiceFlow: () => false,
  getMaxAllowedItems: () => 0,
  isAtItemLimit: () => false,
  getSkuPageConfig: () => ({ showWarning: false }),
  getStandardHeaderConfig: () => ({ showCartIcon: true, onCartIconClick: null }),
};

/**
 * createCartFlow()
 *
 * Creates a set of utility functions to manage cart flow behavior based on the current cart state.
 * This function is designed to handle both single-service and multi-service scenarios across
 * multiple applications with different state management solutions (e.g., Redux sagas, Redux thunks, Zustand).
 *
 * Key features:
 * - Determines the current flow type (single-service or multi-service)
 * - Manages item limits based on the flow type
 * - Can be used in components, business logic files, and potentially server-side
 *
 * Usage:
 * const cartFlow = createCartFlow({ cart });
 * if (cartFlow.isAtItemLimit()) {
 *   // Prevent adding more items or show a warning
 * }
 *
 * Note: Consider using the memoized version (memoizedCreateCartFlow) for performance optimization
 * when used in components or other scenarios where it might be called frequently with the same cart state.
 */
export const createCartFlow = ({ cart }: { cart: Cart }): TCartFlow => {
  // #######################
  //    CART STATE UTILS
  // #######################
  const isSingleServiceFlow = () => Boolean(cart?.singleService);

  const getMaxAllowedItems = () => (isSingleServiceFlow() ? MAX_ITEMS.SINGLE_SERVICE : MAX_ITEMS.MULTIPLE_SERVICES);

  const isAtItemLimit = () => (cart?.items?.length || 0) >= getMaxAllowedItems();

  // ##################
  //     UI HELPERS
  // ##################

  const getSkuPageConfig: TCartFlow['getSkuPageConfig'] = ({
    /** Sku to be added to the cart. */
    newSkuId,
    /** Sku in the cart. May be null if there is no sku in the cart. */
    existingSkuId = undefined,
  }) => {
    const isAddingNewSku = Boolean(existingSkuId) && newSkuId !== existingSkuId;
    const showWarning = isSingleServiceFlow() && isAtItemLimit() && isAddingNewSku;
    return { showWarning };
  };

  const getStandardHeaderConfig: TCartFlow['getStandardHeaderConfig'] = () => {
    /**
     * Don't add any callbacks here, the property is more for documentation/typing purposes.
     * The caller should add callbacks if needed.
     *
     * Example:
     *  const baseConfig = getTopNavConfig();
     *  const myCallback = () => {};
     *  const baseConfigEnhanced = { ...baseConfig, onCartIconClick: myCallback };
     */
    const base = { onCartIconClick: null };
    if (isSingleServiceFlow()) {
      const cartHasItems = Boolean(cart?.items?.length);
      const cartHasPlan = Boolean(cart?.plan);
      const showCartIcon = cartHasItems || cartHasPlan;
      return { showCartIcon, ...base };
    }
    return { showCartIcon: true, ...base };
  };

  return {
    // Cart State Utils
    isSingleServiceFlow,
    getMaxAllowedItems,
    isAtItemLimit,

    // UI Helpers
    getSkuPageConfig,
    getStandardHeaderConfig,
  };
};

const resolver = ({ cart }: { cart: Cart }) =>
  JSON.stringify({
    availability: cart.availability,
    breakdownTotal: cart.breakdown?.total,
    clientId: cart.clientId,
    hasOnlyProducts: cart.hasOnlyProducts,
    hasPlan: !!cart.plan,
    hasProducts: cart.hasProducts,
    id: cart.id,
    itemCount: cart.items?.length,
    singleService: cart.singleService,
    skipCart: cart.skipCart,
    statusId: cart.statusId,
    token: cart.token,
  });

/**
 * Memoized version of createCartFlow() for performance.
 *
 * The list of properties in the resolver function are used to
 * determine if the cart has changed in such a way that the
 * cartFlow object needs to be recreated.
 */
export const memoizedCreateCartFlow = memoize(createCartFlow, resolver);
